import { formatMoney } from '@theme/utilities';
import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/**
 * <fan-bundle> : 1 / 2 / 3 fans selector rendered inside <product-form>.
 * - Tier 1 leaves the theme add to cart untouched.
 * - Tiers 2 and 3 intercept the form submit (capture phase, before product-form.js) and add every fan
 *   in one /cart/add.js call, grouped by variant, then dispatch the theme CartUpdatedEvent so the
 *   cart drawer refreshes and opens like a normal add to cart.
 * - Displayed prices mimic a Shopify percentage product discount: truncated to the cent per unit, then x quantity.
 *   The real discount comes from Shopify automatic discounts (minimum quantity 2 and 3).
 */
class FanBundle extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.onChange = this.onChange.bind(this);
    this.onVariantUpdated = this.onVariantUpdated.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  connectedCallback() {
    if (this.closest('quick-add-dialog, product-card, dialog')) {
      this.hidden = true;
      return;
    }

    this.productForm = this.closest('product-form');
    this.tiers = Array.from(this.querySelectorAll('[data-fan-bundle-tier]'));
    this.mainVariantId = String(this.dataset.variantId || '');
    this.mainPrice = parseInt(this.dataset.price, 10) || 0;

    this.addEventListener('change', this.onChange);
    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    this.productForm?.addEventListener('submit', this.onSubmit, true);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated);
    this.productForm?.removeEventListener('submit', this.onSubmit, true);
    this.productForm?.removeAttribute('data-fan-bundle-multi');
  }

  get selectedTier() {
    return this.tiers.find((tier) => tier.querySelector('[data-fan-bundle-radio]')?.checked) || this.tiers[0];
  }

  get selectedQuantity() {
    return parseInt(this.selectedTier?.dataset.qty, 10) || 1;
  }

  onChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('[data-fan-bundle-radio]')) {
      this.render();
    } else if (target.matches('[data-fan-bundle-select]')) {
      target.dataset.touched = 'true';
      this.render();
    }

    // Keep these changes away from the theme form listeners
    event.stopPropagation();
  }

  /**
   * Main variant changed at the top of the page: rows the customer has not customised follow it.
   */
  onVariantUpdated(event) {
    if (!this.productForm || !(event.target instanceof Node) || !this.productForm.contains(event.target)) return;

    const variant = event.detail?.resource;
    if (!variant?.id) return;

    this.mainVariantId = String(variant.id);
    if (Number.isFinite(variant.price)) this.mainPrice = variant.price;

    this.querySelectorAll('[data-fan-bundle-select]').forEach((select) => {
      if (select.dataset.touched === 'true') return;
      const option = Array.from(select.options).find((item) => item.value === this.mainVariantId);
      if (option && !option.disabled) select.value = this.mainVariantId;
    });

    this.render();
  }

  priceOf(id) {
    const option = this.querySelector(`[data-fan-bundle-select] option[value="${id}"]`);
    const price = parseInt(option?.dataset.price, 10);
    return Number.isFinite(price) ? price : this.mainPrice;
  }

  computeTier(tier) {
    const qty = parseInt(tier.dataset.qty, 10) || 1;
    const pct = parseFloat(tier.dataset.pct) || 0;
    const selects = Array.from(tier.querySelectorAll('[data-fan-bundle-select]'));
    const ids = selects.length === qty ? selects.map((select) => select.value) : Array(qty).fill(this.mainVariantId);

    const groups = new Map();
    ids.forEach((id) => {
      const group = groups.get(id) || { id, quantity: 0, price: this.priceOf(id) };
      group.quantity += 1;
      groups.set(id, group);
    });

    let full = 0;
    let discount = 0;
    groups.forEach((group) => {
      const line = group.price * group.quantity;
      full += line;
      // Shopify: percentage discount computed per unit and truncated to the cent, then x quantity
      discount += Math.floor((group.price * pct) / 100) * group.quantity;
    });

    return {
      qty,
      full,
      discount,
      total: full - discount,
      items: Array.from(groups.values()).map(({ id, quantity }) => ({ id: Number(id), quantity })),
    };
  }

  render() {
    const selected = this.selectedTier;

    this.tiers.forEach((tier) => {
      const isSelected = tier === selected;
      tier.classList.toggle('is-selected', isSelected);

      const { full, discount, total } = this.computeTier(tier);

      const price = tier.querySelector('[data-fan-bundle-price]');
      if (price) price.textContent = formatMoney(total);

      const compare = tier.querySelector('[data-fan-bundle-compare]');
      if (compare) compare.textContent = discount > 0 ? formatMoney(full) : '';

      const save = tier.querySelector('[data-fan-bundle-save]');
      if (save) {
        const text = this.dataset.saveText || 'Save [amount]';
        save.textContent = discount > 0 ? text.replace('[amount]', formatMoney(discount)) : '';
      }

      const rows = tier.querySelector('[data-fan-bundle-rows]');
      if (rows) rows.hidden = !isSelected;
    });

    // Hides the theme quantity selector and express checkout while 2 or 3 fans are selected (see block CSS)
    this.productForm?.toggleAttribute('data-fan-bundle-multi', this.selectedQuantity > 1);
  }

  onSubmit(event) {
    // Tier 1: normal theme behaviour
    if (this.hidden || this.selectedQuantity <= 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy) return;

    const { items } = this.computeTier(this.selectedTier);
    if (!items.length) return;
    this.busy = true;

    this.productForm.querySelector('[data-ref="add-to-cart-button"]')?.classList.add('is-loading');

    const cartDrawer = document.querySelector('cart-drawer');
    const sections = cartDrawer?.dataset.sectionId ? [cartDrawer.dataset.sectionId] : [];
    const cartIcon = document.querySelector('cart-icon');
    const behavior = cartIcon?.addToCartBehavior || 'open_cart';
    const addedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sections.join(',') }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Error');
        return data;
      })
      .then((data) => {
        const currentCount = cartIcon?.getCurrentItemCount?.();
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: currentCount != null ? currentCount + addedQuantity : undefined,
              source: 'product-form',
              sections: data.sections,
            },
          ),
        );

        if (behavior === 'notification') {
          document.dispatchEvent(
            new CustomEvent('toast:open', {
              detail: { type: 'success', message: Theme.translations?.addToCartNotificationMessage },
            }),
          );
        }
      })
      .catch((error) => {
        console.error('Fan bundle:', error);
        document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
      })
      .finally(() => {
        this.busy = false;
        this.productForm.querySelector('[data-ref="add-to-cart-button"]')?.classList.remove('is-loading');
      });
  }
}

if (!customElements.get('fan-bundle')) {
  customElements.define('fan-bundle', FanBundle);
}
